<template>
  <section
    class="flex min-h-0 w-72 shrink-0 flex-col border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900"
    @dragover.prevent="handleColumnDragover"
    @drop.prevent="handleDrop(tasks.length)"
  >
    <header class="flex min-h-11 items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
      <div class="flex min-w-0 items-center gap-2">
        <h2 class="truncate text-xs font-semibold uppercase tracking-normal text-slate-600 dark:text-slate-300">{{ label }}</h2>
        <button
          type="button"
          class="flex h-6 w-6 shrink-0 items-center justify-center text-slate-500 hover:bg-white hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          title="Add task"
          @click="startCreate"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
            <path d="M8 3.5v9M3.5 8h9" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <span class="border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        {{ tasks.length }}
      </span>
    </header>

    <form
      v-if="creating"
      class="flex items-center gap-1 border-b border-slate-200 bg-white px-2 py-2 dark:border-slate-800 dark:bg-slate-900"
      @submit.prevent="submit"
    >
      <input
        ref="titleInput"
        v-model.trim="title"
        type="text"
        class="h-8 min-w-0 flex-1 border border-slate-300 bg-white px-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder="Task title"
        :disabled="submitting"
        @keydown.escape.prevent="cancel"
      >
      <button
        type="submit"
        class="h-8 bg-slate-900 px-2.5 text-xs font-semibold text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-600 dark:hover:bg-indigo-500"
        :disabled="submitting"
      >
        Add
      </button>
    </form>

    <p v-if="error" class="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
      {{ error }}
    </p>

    <div class="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
      <div
        v-for="(task, index) in tasks"
        :key="task.id"
        :class="taskContainerClass(task)"
        @dragover.prevent.stop="handleCardDragover($event, index)"
        @drop.prevent.stop="handleDrop(dropIndex)"
      >
        <div
          v-if="dropIndex === index"
          class="mb-2 h-0.5 bg-indigo-600"
        />
        <TaskCard
          :store="store"
          :task="task"
          :held-token="store.heldToken(task.id)"
          @dragstart="$emit('dragstart', task)"
        />
      </div>
      <div
        v-if="dropIndex === tasks.length"
        class="h-0.5 bg-indigo-600"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import TaskCard from './TaskCard.vue'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  tasks: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['dragstart', 'reorder'])
const dropIndex = ref(null)
const creating = ref(false)
const submitting = ref(false)
const title = ref('')
const error = ref('')
const titleInput = ref(null)
const dragDisabled = computed(() => Boolean(props.store.projectFilterId.value))

function taskContainerClass(task) {
  const lockedByOther = task.locked && !props.store.heldToken(task.id)
  return task.planning_state === 'parked' && !lockedByOther ? 'opacity-60' : ''
}

async function startCreate() {
  creating.value = true
  error.value = ''
  await nextTick()
  titleInput.value?.focus()
}

function cancel() {
  creating.value = false
  title.value = ''
  error.value = ''
}

async function submit() {
  if (!title.value || submitting.value) return

  submitting.value = true
  error.value = ''

  try {
    const attributes = {
      title: title.value,
      status: props.status,
    }

    if (props.store.projectFilterId.value) {
      attributes.project_id = props.store.projectFilterId.value
    }

    await props.store.createTask(attributes)
    cancel()
  } catch (requestError) {
    error.value = requestError?.message || 'Task could not be created'
  } finally {
    submitting.value = false
  }
}

function handleCardDragover(event, index) {
  if (dragDisabled.value) {
    dropIndex.value = null
    return
  }

  const rect = event.currentTarget.getBoundingClientRect()
  const afterMidpoint = event.clientY > rect.top + rect.height / 2
  dropIndex.value = index + (afterMidpoint ? 1 : 0)
}

function handleColumnDragover(event) {
  if (dragDisabled.value) {
    dropIndex.value = null
    return
  }

  if (event.target === event.currentTarget || !props.tasks.length) {
    dropIndex.value = props.tasks.length
  }
}

function handleDrop(position) {
  if (dragDisabled.value) {
    dropIndex.value = null
    return
  }

  emit('reorder', { status: props.status, position })
  dropIndex.value = null
}
</script>
